import { useUserStore } from '@/stores/user'
import axios, { AxiosError } from 'axios'
import type { LoginResponse, SSOInformations, TenantInfo } from '@/types/auth'

interface LoginCredentials {
  login: string
  password: string
  code: string
}

export class AuthService {
  private baseURL = '/api/v1'
  private tokenKey = 'auth_token'
  private refreshTokenKey = 'auth_refresh_token'
  private codeKey = 'auth_code'
  private tenantKey = 'current_tenant'
  private tenantsKey = 'tenants'
  private userKey = 'current_user'
  private tokenCheckInterval: number | null = null

  constructor() {
    axios.interceptors.request.use((config) => {
      const tenant = this.getTenant()

      if (tenant && config.headers) {
        config.headers['X-Tenant'] = tenant
      }

      if (config.headers) {
        config.headers['Content-Type'] = 'application/json'
        config.headers['Accept'] = 'application/json'
      }

      return config
    })

    // Debug-Interceptor
    axios.interceptors.request.use(request => {
      return request
    })

    axios.interceptors.response.use(response => {
      return response
    }, error => {
      return Promise.reject(error)
    })
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const loginData = {
        login: credentials.login.trim().includes('@')
          ? credentials.login.trim()
          : credentials.login.trim().toUpperCase(),
        password: credentials.password,
        code: credentials.code,
      }

      const response = await axios.post<LoginResponse>(`${this.baseURL}/account/login`, loginData, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        withCredentials: true
      })

      if (response.data.success) {
        this.setCode(credentials.code);

        if (response.data.tenants?.length === 1) {
          this.setTenant(response.data.tenants[0].tenant)
        }

        // Speichere Tenanten
        this.setTenants(response.data.tenants)

        // Speichere Benutzerinformationen
        const userStore = useUserStore()
        userStore.setUserInfo(response.data)

        // Speichere zusätzliche Benutzerinformationen
        localStorage.setItem(this.userKey, JSON.stringify(response.data))
      }

      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {

        if (error.response?.status === 401) {
          throw new Error('Invalid Login Data')
        }
      }
      throw error
    }
  }

  async logout(): Promise<void> {
    try {
      await axios.post(`${this.baseURL}/account/logout`, {})
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      this.stopTokenValidation()
      this.clearToken()
      localStorage.removeItem(this.tenantKey)
      localStorage.removeItem(this.tenantsKey)
      localStorage.removeItem(this.userKey)
      // window.location.href = `${window.location.protocol}://${window.location.host}/en/login`;
    }
  }

  setCode(code?: string): void {
    if (!code) {
      localStorage.removeItem(this.codeKey);
      return;
    }
    localStorage.setItem(this.codeKey, code);
  }

  getCode(): string | null {
    return localStorage.getItem(this.codeKey) || null;
  }

  private isValidJWT(token: string): boolean {
    const jwtRegex = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/
    return jwtRegex.test(token)
  }

  clearToken(): void {
    localStorage.removeItem(this.tokenKey)
  }

  getTenant(): string | null {
    const tenant = localStorage.getItem(this.tenantKey)
    if (!tenant) {
      console.warn('Kein Tenant gefunden')
      return null
    }
    return tenant.toUpperCase()
  }

  getTenants(): TenantInfo[] {
    const tenantStr = localStorage.getItem(this.tenantsKey)
    if (!tenantStr) {
      console.warn('Keine Tenanten-Liste gefunden')
      return []
    }
    try {
      const tenants = JSON.parse(tenantStr)
      if (!Array.isArray(tenants)) {
        console.error('Ungültiges Tenanten-Format')
        return []
      }
      return tenants.map((m: { tenant: string; adminflag: boolean }) => ({
        ...m,
        tenant: m.tenant.toUpperCase()
      }))
    } catch (error) {
      console.error('Fehler beim Parsen der Tenanten:', error)
      return []
    }
  }

  resetTenant(): void {
    localStorage.removeItem(this.tenantKey);
  }

  setTenant(tenant: string): void {
    if (!tenant) {
      console.error('Ungültiger Tenant')
      return
    }

    // Validiere ob der Tenant in der Liste der verfügbaren Tenanten ist
    const availableTenants = this.getTenants()
    const tenantExists = availableTenants.some(
      m => m.tenant.toUpperCase() === tenant.toUpperCase()
    )

    if (!tenantExists) {
      console.error('Tenant nicht in der Liste der verfügbaren Tenanten')
      return
    }

    localStorage.setItem(this.tenantKey, tenant.toUpperCase())
  }

  setTenants(tenants: Array<{ tenant: string; activeFlag: boolean }>): void {
    if (!Array.isArray(tenants)) {
      console.error('Ungültiges Tenanten-Format')
      return
    }

    const validatedTenants = tenants
      .filter(m => m && typeof m.tenant === 'string')
      .map(m => ({
        ...m,
        tenant: m.tenant.toUpperCase()
      }))

    if (validatedTenants.length === 0) {
      console.error('Keine gültigen Tenanten gefunden')
      return
    }

    localStorage.setItem(this.tenantsKey, JSON.stringify(validatedTenants))

    // Wenn nur ein Tenant verfügbar ist, setze diesen automatisch
    if (validatedTenants.length === 1 && !this.getTenant()) {
      this.setTenant(validatedTenants[0].tenant)
    }
  }

  isAdmin(): boolean {
    const user = this.getCurrentUser()
    console.log('Current user data:', user)

    // System Admin Check (adminflag ist ein boolean)
    const isSystemAdmin = user?.adminflag === true

    // Tenanten Admin Check
    const currentTenant = this.getTenant()
    const currentTenantData = this.getTenants().find(
      m => m.tenant.toUpperCase() === currentTenant?.toUpperCase()
    )
    const isTenantAdmin = currentTenantData?.adminflag === true

    console.log('Admin check:', { isSystemAdmin, isTenantAdmin, currentTenant })
    return isSystemAdmin || isTenantAdmin
  }

  isSupport(): boolean {
    // Implementierung der Support-Prüfung
    return false // Anpassen nach Ihrer Logik
  }

  getCurrentUser() {
    const userStr = localStorage.getItem(this.userKey)
    console.log('Raw user data from localStorage:', userStr)
    if (!userStr) {
      console.warn('Keine Benutzerdaten im localStorage gefunden')
      return null
    }
    try {
      const user = JSON.parse(userStr)
      console.log('Parsed user data:', user)
      return user
    } catch (error) {
      console.error('Fehler beim Parsen der Benutzerdaten:', error)
      return null
    }
  }

  async reset2FA(password: string): Promise<void> {
    try {
      await axios.post(`${this.baseURL}/account/reset-2fa`, { password }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('Ungültiges Passwort')
        }
      }
      throw error
    }
  }

  async changePassword(data: { password: string, newPassword: string }): Promise<void> {
    try {
      await axios.post(`${this.baseURL}/account/pwd`, data, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('Aktuelles Passwort ist ungültig')
        } else if (error.response?.status === 400) {
          throw new Error('Das neue Passwort entspricht nicht den Anforderungen')
        }
      }
      throw error
    }
  }

  async refreshToken() {
    try {
      await axios.post<LoginResponse>(`${this.baseURL}/account/refreshtoken`, {
        code: this.getCode(),
      });
    } catch (error) {
      console.log('error while refreshing token', error);
      this.logout();
    }
  }

  private stopTokenValidation() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval)
      this.tokenCheckInterval = null
    }
  }

  async getSSOInformations(userName: string, redirectUri: string): Promise<SSOInformations> {
    try {

      const response = await axios.get<SSOInformations>(`${this.baseURL}/account/sso/information`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        withCredentials: true,
        params: {
          userName,
          redirectUri,
        }
      })

      console.log(response.data)

      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.log(error);
      }
      throw error
    }
  }
}
